import React, { FunctionComponent, useState, useEffect } from "react"
import { User, Company, Prompt, TransactionsSortFields, Sort } from "../../../typings/types"
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel
} from "@material-ui/core"
import {
    ApproveAudioMetadata,
    CreateAudioMetadata,
    TranslateTextMetadata
} from "../../common/interfaces"
import { TransactionGroup } from "./TransactionGroup"
import { SortDirections, toggleSortDirection } from "../../common/SortUtilities"
import _ from "lodash"

export type TransactionMetadata = TranslateTextMetadata | CreateAudioMetadata | ApproveAudioMetadata

export interface TransactionModel {
    id: number
    referenceId: string
    action: string
    mode: string
    metadata: TransactionMetadata
    user?: User
    company?: Company
    prompt?: Prompt
    createdAt?: string
    updatedAt?: string
}

enum SortFields {
    MODE = "mode",
    CREATED_AT = "createdAt",
    USER = "user"
}

export interface TransactionsTableProps {
    transactions: TransactionModel[]
    setActiveSort: React.Dispatch<React.SetStateAction<TransactionsSortFields>>
}

export const TransactionsTable: FunctionComponent<TransactionsTableProps> = ({
    transactions,
    setActiveSort
}) => {
    const [activeSortField, setActiveSortField] = useState<SortFields>(SortFields.CREATED_AT)
    const [activeSortDirection, setActiveSortDirection] = useState<SortDirections>(
        SortDirections.DESC
    )

    const toggleSort = (field: SortFields): void => {
        if (activeSortField !== field) setActiveSortField(field)
        else toggleSortDirection(setActiveSortDirection, activeSortDirection)
    }

    useEffect(() => {
        // See TTPSP-404 for removing type casting here
        const newActiveSort = {} as TransactionsSortFields
        const newActiveSortDirection = (activeSortDirection as unknown) as Sort
        newActiveSort[activeSortField] = newActiveSortDirection
        setActiveSort(newActiveSort)
    }, [activeSortField, activeSortDirection])

    const transactionGroups = _.groupBy(transactions, "referenceId")
    return (
        <TableContainer>
            <Table id="transactionsTable" data-testid="transactionsTable">
                <TableHead>
                    <TableRow>
                        <TableCell />
                        <TableCell>Reference ID</TableCell>
                        <TableCell>
                            <TableSortLabel
                                active={activeSortField === SortFields.USER}
                                direction={activeSortDirection}
                                onClick={() => toggleSort(SortFields.USER)}
                            >
                                User
                            </TableSortLabel>
                        </TableCell>
                        <TableCell>
                            <TableSortLabel
                                active={activeSortField === SortFields.MODE}
                                direction={activeSortDirection}
                                onClick={() => toggleSort(SortFields.MODE)}
                            >
                                Type
                            </TableSortLabel>
                        </TableCell>
                        <TableCell>
                            <TableSortLabel
                                active={activeSortField === SortFields.CREATED_AT}
                                direction={activeSortDirection}
                                onClick={() => toggleSort(SortFields.CREATED_AT)}
                            >
                                Created (UTC)
                            </TableSortLabel>
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody id="transactionsBody">
                    {Object.keys(transactionGroups).map(key => (
                        <TransactionGroup key={key} transactions={transactionGroups[key]} />
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    )
}
